import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Calendar, Check, Loader2 } from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [dateCheckins, setDateCheckins] = useState([]);
  const [dateLoading, setDateLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/checkins/history?limit=30`);
      setHistory(res.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchByDate = async (date) => {
    setDateLoading(true);
    try {
      const res = await axios.get(`${API}/checkins/history?date=${date}&limit=50`);
      setDateCheckins(res.data);
    } catch {
      toast.error('Erro ao carregar');
    } finally {
      setDateLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) fetchByDate(date);
    else setDateCheckins([]);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div></div>;
  }

  const chartData = history.reduce((acc, checkin) => {
    const date = new Date(checkin.timestamp).toLocaleDateString('pt-BR');
    if (!acc.find(d => d.date === date)) acc.push({ date, count: 1 });
    return acc;
  }, []).reverse();

  const displayCheckins = selectedDate ? dateCheckins : history;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Histórico de Frequência</h1>
        <p className="text-slate-600 mt-1">Seus check-ins</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-100"><CardContent className="p-5">
          <div className="text-slate-500 text-sm mb-1">Vínculo</div>
          <div className="text-3xl font-bold text-slate-900">{history.filter(h => !h.is_extra_checkin).length}</div>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-5">
          <div className="text-slate-500 text-sm mb-1">Extras</div>
          <div className="text-3xl font-bold text-emerald-600">{history.filter(h => h.is_extra_checkin).length}</div>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-5">
          <div className="text-slate-500 text-sm mb-1">Total (30d)</div>
          <div className="text-3xl font-bold text-slate-900">{history.length}</div>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-5">
          <div className="text-slate-500 text-sm mb-1">Média/Sem.</div>
          <div className="text-3xl font-bold text-slate-900">{Math.round((history.length / 4) * 10) / 10}</div>
        </CardContent></Card>
      </div>

      <Card className="border-slate-100">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-medium text-slate-700">Filtrar por data:</Label>
            <Input type="date" value={selectedDate} onChange={(e) => handleDateChange(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              min={new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0]}
              className="max-w-[200px]" data-testid="student-history-date-picker" />
            {selectedDate && (
              <Button variant="ghost" size="sm" onClick={() => handleDateChange('')} className="text-xs text-slate-500">Limpar filtro</Button>
            )}
            {selectedDate && (
              <span className="text-sm text-slate-500">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                {' - '}{dateCheckins.length} check-in(s)
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {chartData.length > 0 && !selectedDate && (
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Frequência</CardTitle></CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs><linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '12px', boxShadow: '0 8px 30px rgba(15,23,42,0.12)' }} />
                  <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#colorCount)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {dateLoading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : displayCheckins.length === 0 ? (
        <Card className="border-slate-100"><CardContent className="p-12 text-center">
          <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{selectedDate ? 'Nenhum check-in nesta data' : 'Nenhum check-in ainda'}</h3>
          <p className="text-slate-500">{selectedDate ? 'Selecione outra data para ver seus check-ins.' : 'Comece a treinar e seus check-ins aparecerão aqui.'}</p>
        </CardContent></Card>
      ) : (
        <Card className="border-slate-100"><CardContent className="p-0">
          <div className="divide-y divide-slate-100">
            {displayCheckins.map((checkin) => (
              <div key={checkin.checkin_id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">
                      {new Date(checkin.timestamp).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </div>
                    <div className="text-sm text-slate-500">
                      {new Date(checkin.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      {checkin.gym_name && <span className="ml-2 text-slate-400">| {checkin.gym_name}</span>}
                      {checkin.modality && <span className="ml-2 text-emerald-600 font-medium">{checkin.modality}</span>}
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  checkin.is_extra_checkin ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {checkin.is_extra_checkin ? 'Extra' : 'Vínculo'}
                </span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

export default HistoryPage;
